/**
 * The themes of a release (spec `docs/superpowers/specs/2026-10-05-music24-release-themes-design.md`).
 * A release has one theme: a feeling. The theme owns the director brief, the fonts and the type
 * style, the colour recipe, the texture, the wash and the cover treatment of every asset of the
 * song (kit images, kit videos, the AI reel). The theme is binding: the renderer and the plan
 * checks apply it, the director only works inside it.
 *
 * The technique of one asset is its style (`assetStyles.ts`), not a part of the theme.
 *
 * The values are the approved design (Claude Design "q Release Themes"; the copy is
 * `docs/superpowers/design-reference/music24-release-kit/release-themes.values.json`). Change a
 * theme value here, nowhere else, and keep it equal to the design. The file has no imports, so
 * every project can use it. The string unions below are twins of the schema constants in
 * music24-api; a test there compares them (`releaseThemeEnums.test.ts`).
 */

// ---------------------------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------------------------

export const KIT_FONT_IDS = [
  'grotesk',
  'serif',
  'mono',
  'hand',
  'condensed',
  'italic',
  'didone',
  'script',
  'wide',
] as const;
export type KitFontId = (typeof KIT_FONT_IDS)[number];

export interface KitFontFace {
  /** The CSS family. The `M24 Kit` prefix keeps a system font of the same name from taking its place. */
  family: string;
  /** The generic CSS fallback. */
  fallback: string;
  /** The font file of the renderer, in `music24-api/src/video/static/fonts/`. All are OFL 1.1. */
  file: string;
  /** The lowest and the highest weight of the font file. A file with one weight has the same value two times. */
  weights: readonly [number, number];
  /** True for a file that holds only the italic (Instrument Serif Italic). */
  italic: boolean;
  /** The weight of the font as a display font. A font override uses it. */
  displayWeight: number;
  /** How wide the font runs, relative to Inter Tight. A font override scales its size by it. */
  relativeWidth: number;
}

/**
 * The face of each kit font. The renderer (`kitFonts.ts` in music24-api) and the web app use the
 * same family names.
 */
export const KIT_FONT_FACES: Readonly<Record<KitFontId, KitFontFace>> = {
  grotesk: {
    family: 'M24 Kit Inter Tight',
    fallback: 'sans-serif',
    file: 'InterTight.ttf',
    weights: [100, 900],
    italic: false,
    displayWeight: 700,
    relativeWidth: 1,
  },
  serif: {
    family: 'M24 Kit Fraunces',
    fallback: 'serif',
    file: 'Fraunces.ttf',
    weights: [100, 900],
    italic: false,
    displayWeight: 700,
    relativeWidth: 1.05,
  },
  mono: {
    family: 'M24 Kit JetBrains Mono',
    fallback: 'monospace',
    file: 'JetBrainsMono.ttf',
    weights: [100, 800],
    italic: false,
    displayWeight: 700,
    relativeWidth: 1.25,
  },
  hand: {
    family: 'M24 Kit Caveat',
    fallback: 'cursive',
    file: 'Caveat.ttf',
    weights: [400, 700],
    italic: false,
    displayWeight: 700,
    relativeWidth: 0.72,
  },
  condensed: {
    family: 'M24 Kit Anton',
    fallback: 'sans-serif',
    file: 'Anton-Regular.ttf',
    weights: [400, 400],
    italic: false,
    displayWeight: 400,
    relativeWidth: 0.74,
  },
  italic: {
    family: 'M24 Kit Instrument Serif Italic',
    fallback: 'serif',
    file: 'InstrumentSerif-Italic.ttf',
    weights: [400, 400],
    italic: true,
    displayWeight: 400,
    relativeWidth: 0.8,
  },
  didone: {
    family: 'M24 Kit Playfair Display',
    fallback: 'serif',
    file: 'PlayfairDisplay.ttf',
    weights: [400, 900],
    italic: false,
    displayWeight: 600,
    relativeWidth: 1.1,
  },
  script: {
    family: 'M24 Kit Shrikhand',
    fallback: 'cursive',
    file: 'Shrikhand-Regular.ttf',
    weights: [400, 400],
    italic: false,
    displayWeight: 400,
    relativeWidth: 1.05,
  },
  wide: {
    family: 'M24 Kit Unbounded',
    fallback: 'sans-serif',
    file: 'Unbounded.ttf',
    weights: [200, 900],
    italic: false,
    displayWeight: 800,
    relativeWidth: 1.5,
  },
};

export function isKitFontId(value: unknown): value is KitFontId {
  return typeof value === 'string' && (KIT_FONT_IDS as readonly string[]).includes(value);
}

/** The CSS `font-family` of a kit font, with its generic fallback. */
export function kitFontCssFamily(fontId: KitFontId): string {
  const face = KIT_FONT_FACES[fontId];
  return `'${face.family}', ${face.fallback}`;
}

// ---------------------------------------------------------------------------------------------
// The enum twins of the plan schemas (music24-api)
// ---------------------------------------------------------------------------------------------

/** How one shot starts. `dissolve` fades the new shot in over the old one. */
export const THEME_TRANSITIONS = ['cut', 'flash', 'punch', 'dissolve'] as const;
export type ThemeTransition = (typeof THEME_TRANSITIONS)[number];

/** The camera move of a bank clip. */
export const THEME_SEGMENT_MOTIONS = ['static', 'push', 'pull', 'pan-left', 'pan-right'] as const;
export type ThemeSegmentMotion = (typeof THEME_SEGMENT_MOTIONS)[number];

/** How the cover moves. */
export const THEME_ARTWORK_MOTIONS = ['drift', 'pulse', 'liquid', 'still'] as const;
export type ThemeArtworkMotion = (typeof THEME_ARTWORK_MOTIONS)[number];

export const THEME_CAPTION_STYLES = ['word', 'line', 'kinetic'] as const;
export type ThemeCaptionStyle = (typeof THEME_CAPTION_STYLES)[number];

export const THEME_WAVEFORM_STYLES = ['bars', 'line', 'ring'] as const;
export type ThemeWaveformStyle = (typeof THEME_WAVEFORM_STYLES)[number];

export const THEME_AUDIO_REACTIVE_STYLES = ['bars', 'ring', 'particles'] as const;
export type ThemeAudioReactiveStyle = (typeof THEME_AUDIO_REACTIVE_STYLES)[number];

/** How a text enters the frame. The renderer owns the motion of each one. */
export const THEME_TEXT_ANIMATIONS = ['fade', 'rise', 'slam', 'blurIn', 'typewriter', 'wiggle'] as const;
export type ThemeTextAnimation = (typeof THEME_TEXT_ANIMATIONS)[number];

export const THEME_TEXTURES = ['none', 'grain', 'glow', 'paper'] as const;
export type ThemeTextureKind = (typeof THEME_TEXTURES)[number];

/** The CSS blend mode of the wash. `saturation` with a near-black colour makes the picture grey (Noir). */
export const THEME_WASH_BLENDS = ['screen', 'soft-light', 'saturation'] as const;
export type ThemeWashBlend = (typeof THEME_WASH_BLENDS)[number];

/** The frame of the cover card: the CSS of each one is in the renderer (`ReelCoverCard.tsx`). */
export const THEME_COVER_FRAMES = ['card', 'sticker', 'soft', 'plate', 'arch', 'sleeve', 'tube', 'photo'] as const;
export type ThemeCoverFrame = (typeof THEME_COVER_FRAMES)[number];

/** The ground of a frame with no full picture: one colour, or a turn from the background to the surface. */
export const THEME_GROUNDS = ['flat', 'gradient'] as const;
export type ThemeGround = (typeof THEME_GROUNDS)[number];

/** A fixed decoration of the theme, drawn by the renderer (`ReelOrnament.tsx`). */
export const THEME_ORNAMENTS = ['hairline', 'bar', 'line', 'bandsRecord', 'tornTape'] as const;
export type ThemeOrnament = (typeof THEME_ORNAMENTS)[number];

/** A CSS effect on a text: a box behind the kicker, a hard shadow, a glow, or a marker underline of the title. */
export const THEME_TEXT_EFFECTS = ['label', 'hardShadow', 'glow', 'marker'] as const;
export type ThemeTextEffect = (typeof THEME_TEXT_EFFECTS)[number];

export const THEME_TEXT_CASES = ['none', 'uppercase', 'lowercase'] as const;
export type ThemeTextCase = (typeof THEME_TEXT_CASES)[number];

/** Twins of the axes of `reelBankTaxonomy.ts`; the test of this file compares them. */
export type ThemeFootageMood = 'euphoric' | 'melancholic' | 'aggressive' | 'chill' | 'romantic' | 'mysterious';
export type ThemeFootageSetting =
  | 'city-night'
  | 'nature'
  | 'studio'
  | 'abstract'
  | 'road'
  | 'interior'
  | 'water'
  | 'sky';
export type ThemeFootageSubject = 'no-people' | 'people' | 'performer' | 'crowd';
export type ThemeFootageShotType = 'wide' | 'medium' | 'macro' | 'aerial' | 'tracking' | 'slow-motion';
export type ThemeFootageMotion = 'static' | 'slow' | 'medium' | 'fast';

// ---------------------------------------------------------------------------------------------
// The theme
// ---------------------------------------------------------------------------------------------

/** The themes in the order of the picker. */
export const RELEASE_THEME_IDS = ['clean', 'hype', 'dreamy', 'dark', 'warm', 'retro', 'neon', 'handmade'] as const;
export type ReleaseThemeId = (typeof RELEASE_THEME_IDS)[number];

/** The theme of a kit, a reel or an account default that holds no valid theme. */
export const DEFAULT_RELEASE_THEME: ReleaseThemeId = 'clean';

/** The four slots of the theme colours. */
export const THEME_COLOUR_SLOTS = ['background', 'surface', 'text', 'accent'] as const;
export type ThemeColourSlot = (typeof THEME_COLOUR_SLOTS)[number];

/** The cover colour that a rule starts from: the lightest, the middle one by lightness, the darkest, or the one with the most chroma. */
export type ColourRole = 'light' | 'mid' | 'dark' | 'vivid';

/**
 * How one colour slot is made from a cover colour, in OKLCH. `L` is the lightness to set. The
 * chroma is `C` when it is there, else the chroma of the source times `Cx` (default 1), then kept
 * between `Cmin` and `Cmax`. `Hadd` adds degrees to the hue; `Hto` moves the hue a share of the
 * short way to a target hue (`[target degrees, share 0..1]`).
 */
export interface ColourRule {
  from: ColourRole;
  L: number;
  C?: number;
  Cx?: number;
  Cmin?: number;
  Cmax?: number;
  Hadd?: number;
  Hto?: readonly [number, number];
}

export type ColourRecipe = Readonly<Record<ThemeColourSlot, ColourRule>>;

/** The weight, the slant, the case and the letter spacing (in em) of a font role. */
export interface ThemeTypeStyle {
  weight: number;
  italic: boolean;
  case: ThemeTextCase;
  tracking: number;
}

/**
 * How the sung word looks when a lyric lights its words (design u4): `accent` the accent colour,
 * `label` an accent box (Hype), `focus` the accent and a short focus and rise (Dreamy), `hairline`
 * the text with an accent line under the word (Noir), `glow` the accent with a glow (Neon), `shadow`
 * the text with an accent drop shadow (Retro), `marker` a highlighter under the sung part (Handmade).
 */
export const THEME_LYRIC_NOW_WORDS = ['accent', 'label', 'focus', 'hairline', 'glow', 'shadow', 'marker'] as const;
export type ThemeLyricNowWord = (typeof THEME_LYRIC_NOW_WORDS)[number];

/**
 * The lyric motion of a theme (design u, "rolling column"): the lyric lines move as one column.
 * The next line waits small under the sung line and arrives in the middle exactly at its sung
 * start; the sung line is at full alpha from its start to its end. Every alpha is a share of the
 * text colour.
 */
export interface ThemeLyricMotion {
  /** The time of one move of the column, in ms. A move ends at the sung start of the line. */
  moveMs: number;
  /** The curve of the move: the four values of a cubic Bezier (`Easing.bezier` of Remotion). */
  easing: readonly [number, number, number, number];
  /** The alpha of the next line. A word that is not sung yet keeps this alpha in the sung line. */
  nextAlpha: number;
  /** The alpha of the line before, until the next move takes it out of the column. */
  prevAlpha: number;
  /** The scale of a line that is not sung (the next line and the line before). */
  dimScale: number;
  /** How the sung word looks when the lyric lights its words. */
  nowWord: ThemeLyricNowWord;
  /** The move steps on every n-th frame (Retro, like old film); null: on every frame. */
  stepFrames: number | null;
  /** A static turn per line in degrees, for the even and the odd lines (Handmade); null: none. */
  tiltDeg: readonly [number, number] | null;
  /** The sung line sways by this many degrees (Handmade); null: no sway. */
  swayDeg: number | null;
}

export interface ReleaseThemeBrief {
  /** One sentence for the director. */
  feeling: string;
  /** The shortest and the longest shot of a video, in seconds. */
  paceSecPerCut: readonly [number, number];
  /** Where the cuts go. */
  cutOn: 'beat' | 'downbeat' | 'phrase';
  /** The allowed transitions. The first one replaces a transition that is not allowed. */
  transitions: readonly ThemeTransition[];
  /** The allowed camera moves of a bank clip. The first one replaces a move that is not allowed. */
  segmentMotions: readonly ThemeSegmentMotion[];
  /** How the cover can move when the style does not fix it. The first one is the fallback. */
  artworkMotions: readonly ThemeArtworkMotion[];
  /** The bank clips that fit: taxonomy words of `reelBankTaxonomy.ts` and an energy range (1..5). */
  footage: {
    moods: readonly ThemeFootageMood[];
    settings: readonly ThemeFootageSetting[];
    subjects: readonly ThemeFootageSubject[];
    shotTypes: readonly ThemeFootageShotType[];
    motions: readonly ThemeFootageMotion[];
    energy: readonly [number, number];
  };
  /** The style of every reel caption. */
  captionStyle: ThemeCaptionStyle;
  /** How every text layer, the title card and the end card enter. */
  textAnimation: ThemeTextAnimation;
  /** The waveform shape of an asset that has none of its own (`assetStyles.ts`). */
  waveform: ThemeWaveformStyle;
  /** The audio-reactive layer that fits the theme. */
  audioReactive: ThemeAudioReactiveStyle;
  /** How the lines of a lyric video, a lyric moment and the music video captions move (design u4). */
  lyricMotion: ThemeLyricMotion;
}

export interface ReleaseTheme {
  id: ReleaseThemeId;
  /** The English name. The UI shows a react-intl message, not this text. The name of `dark` is Noir. */
  label: string;
  /** One line under the name in the picker (English; the UI shows a react-intl message). */
  tileLine: string;
  brief: ReleaseThemeBrief;
  /**
   * The display font (the title, the CTA, the end card headline) and the text font (the artist, the
   * kicker, the date, a lyric line, the captions), each with its type style.
   */
  type: {
    display: KitFontId;
    text: KitFontId;
    displayStyle: ThemeTypeStyle;
    textStyle: ThemeTypeStyle;
    /**
     * True: the title repeats on a second line in the accent colour (design q: Hype and Neon, the
     * `::after` line of the tile). `titleLines` holds the rule; the renderer and the web still use it.
     */
    accentSecondLine: boolean;
  };
  colours: ColourRecipe;
  /** `strength` (0..1) scales the texture. */
  texture: { kind: ThemeTextureKind; strength: number };
  /** One colour over the picture layers and under the text, with a blend mode. Null: no wash. */
  wash: { colour: ThemeColourSlot; blend: ThemeWashBlend; opacity: number } | null;
  /** How the theme shows the cover as a card, the ground of a frame with no full picture, and its decoration. */
  cover: {
    frame: ThemeCoverFrame;
    ground: ThemeGround;
    ornament: ThemeOrnament | null;
    /** A static turn in degrees of the cover card and of the kicker or the title. Null: no tilt. */
    tiltDeg: { cover?: number; kicker?: number; title?: number } | null;
    textEffect: ThemeTextEffect | null;
  };
}

/** The theme table: the values of the approved design (`release-themes.values.json`). */
export const RELEASE_THEMES: Readonly<Record<ReleaseThemeId, ReleaseTheme>> = {
  clean: {
    id: 'clean',
    label: 'Clean',
    tileLine: 'Calm and clear. The cover leads.',
    brief: {
      feeling: 'Calm and clear. The cover and the title, nothing more.',
      paceSecPerCut: [2, 4],
      cutOn: 'downbeat',
      transitions: ['cut'],
      segmentMotions: ['static', 'push'],
      artworkMotions: ['still', 'drift'],
      footage: {
        moods: ['chill', 'euphoric'],
        settings: ['studio', 'abstract', 'sky'],
        subjects: ['no-people', 'performer'],
        shotTypes: ['wide', 'medium'],
        motions: ['static', 'slow'],
        energy: [2, 4],
      },
      captionStyle: 'line',
      textAnimation: 'fade',
      waveform: 'bars',
      audioReactive: 'bars',
      lyricMotion: {
        moveMs: 420,
        easing: [0.33, 1, 0.68, 1],
        nextAlpha: 0.62,
        prevAlpha: 0.42,
        dimScale: 0.5,
        nowWord: 'accent',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'grotesk',
      text: 'grotesk',
      displayStyle: { weight: 700, italic: false, case: 'none', tracking: -0.025 },
      textStyle: { weight: 500, italic: false, case: 'none', tracking: 0.0 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'light', L: 0.955, Cx: 0.3 },
      surface: { from: 'light', L: 0.905, Cx: 0.4 },
      text: { from: 'dark', L: 0.2, Cx: 0.6 },
      accent: { from: 'vivid', L: 0.5, Cx: 1.1, Cmin: 0.09 },
    },
    texture: { kind: 'none', strength: 0 },
    wash: null,
    cover: {
      frame: 'card',
      ground: 'flat',
      ornament: 'hairline',
      tiltDeg: null,
      textEffect: null,
    },
  },
  hype: {
    id: 'hype',
    label: 'Hype',
    tileLine: 'Loud and fast. Big words on the beat.',
    brief: {
      feeling: 'Loud and fast. Big words that hit on the beat.',
      paceSecPerCut: [0.4, 1.5],
      cutOn: 'beat',
      transitions: ['cut', 'flash', 'punch'],
      segmentMotions: ['push', 'pull', 'pan-left', 'pan-right'],
      artworkMotions: ['pulse'],
      footage: {
        moods: ['euphoric', 'aggressive'],
        settings: ['city-night', 'studio', 'road'],
        subjects: ['performer', 'crowd'],
        shotTypes: ['tracking', 'aerial'],
        motions: ['fast', 'medium'],
        energy: [4, 5],
      },
      captionStyle: 'word',
      textAnimation: 'slam',
      waveform: 'bars',
      audioReactive: 'bars',
      lyricMotion: {
        moveMs: 180,
        easing: [0.34, 1.56, 0.64, 1],
        nextAlpha: 0.7,
        prevAlpha: 0.45,
        dimScale: 0.46,
        nowWord: 'label',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'condensed',
      text: 'grotesk',
      displayStyle: { weight: 400, italic: false, case: 'uppercase', tracking: -0.005 },
      textStyle: { weight: 800, italic: false, case: 'uppercase', tracking: 0.02 },
      accentSecondLine: true,
    },
    colours: {
      background: { from: 'vivid', L: 0.66, Cx: 1.8, Cmin: 0.19 },
      surface: { from: 'dark', L: 0.13, Cx: 0.5 },
      text: { from: 'dark', L: 0.13, Cx: 0.5 },
      accent: { from: 'light', L: 0.97, Cx: 0.3 },
    },
    texture: { kind: 'none', strength: 0 },
    wash: null,
    cover: {
      frame: 'sticker',
      ground: 'flat',
      ornament: 'bar',
      tiltDeg: { cover: 5, kicker: -5 },
      textEffect: 'label',
    },
  },
  dreamy: {
    id: 'dreamy',
    label: 'Dreamy',
    tileLine: 'Soft and slow. Light and haze.',
    brief: {
      feeling: 'Soft and slow. Light, haze and a quiet voice.',
      paceSecPerCut: [2.5, 4],
      cutOn: 'phrase',
      transitions: ['dissolve', 'cut'],
      segmentMotions: ['push', 'static'],
      artworkMotions: ['drift', 'liquid'],
      footage: {
        moods: ['chill', 'romantic', 'melancholic'],
        settings: ['sky', 'water', 'nature'],
        subjects: ['no-people'],
        shotTypes: ['slow-motion', 'aerial', 'wide'],
        motions: ['slow'],
        energy: [1, 3],
      },
      captionStyle: 'kinetic',
      textAnimation: 'blurIn',
      waveform: 'line',
      audioReactive: 'particles',
      lyricMotion: {
        moveMs: 720,
        easing: [0.37, 0, 0.63, 1],
        nextAlpha: 0.6,
        prevAlpha: 0.4,
        dimScale: 0.55,
        nowWord: 'focus',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'italic',
      text: 'grotesk',
      displayStyle: { weight: 400, italic: true, case: 'lowercase', tracking: -0.01 },
      textStyle: { weight: 500, italic: false, case: 'uppercase', tracking: 0.34 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'mid', L: 0.86, Cx: 0.7, Cmax: 0.09 },
      surface: { from: 'mid', L: 0.8, Cx: 0.8, Cmax: 0.1, Hadd: 40 },
      text: { from: 'dark', L: 0.3, Cx: 1 },
      accent: { from: 'vivid', L: 0.52, Cx: 1, Cmin: 0.1, Hadd: -20 },
    },
    texture: { kind: 'glow', strength: 0.6 },
    wash: { colour: 'surface', blend: 'screen', opacity: 0.18 },
    cover: {
      frame: 'soft',
      ground: 'gradient',
      ornament: null,
      tiltDeg: null,
      textEffect: null,
    },
  },
  dark: {
    id: 'dark',
    label: 'Noir',
    tileLine: 'Dark and quiet. Like a film.',
    brief: {
      feeling: 'Dark and quiet. Like the first frame of a film.',
      paceSecPerCut: [1.5, 3],
      cutOn: 'downbeat',
      transitions: ['cut', 'punch'],
      segmentMotions: ['static', 'pull', 'push'],
      artworkMotions: ['drift', 'still'],
      footage: {
        moods: ['mysterious', 'melancholic', 'aggressive'],
        settings: ['city-night', 'interior', 'abstract'],
        subjects: ['no-people', 'people'],
        shotTypes: ['medium', 'macro'],
        motions: ['static', 'slow'],
        energy: [2, 4],
      },
      captionStyle: 'line',
      textAnimation: 'fade',
      waveform: 'ring',
      audioReactive: 'ring',
      lyricMotion: {
        moveMs: 560,
        easing: [0.25, 1, 0.5, 1],
        nextAlpha: 0.55,
        prevAlpha: 0.38,
        dimScale: 0.48,
        nowWord: 'hairline',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'didone',
      text: 'mono',
      displayStyle: { weight: 600, italic: false, case: 'uppercase', tracking: 0.15 },
      textStyle: { weight: 500, italic: false, case: 'uppercase', tracking: 0.34 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'dark', L: 0.11, Cx: 0.4 },
      surface: { from: 'dark', L: 0.18, Cx: 0.4 },
      text: { from: 'light', L: 0.93, Cx: 0.25 },
      accent: { from: 'vivid', L: 0.66, Cx: 0.9 },
    },
    texture: { kind: 'grain', strength: 0.3 },
    wash: { colour: 'background', blend: 'saturation', opacity: 0.85 },
    cover: {
      frame: 'plate',
      ground: 'flat',
      ornament: 'line',
      tiltDeg: null,
      textEffect: null,
    },
  },
  warm: {
    id: 'warm',
    label: 'Warm',
    tileLine: 'Close and golden. Late sun.',
    brief: {
      feeling: 'Close and golden. Late sun in a small room.',
      paceSecPerCut: [2, 4],
      cutOn: 'phrase',
      transitions: ['dissolve', 'cut'],
      segmentMotions: ['pan-left', 'pan-right', 'push'],
      artworkMotions: ['drift'],
      footage: {
        moods: ['romantic', 'chill'],
        settings: ['interior', 'road', 'nature'],
        subjects: ['people', 'no-people'],
        shotTypes: ['medium', 'macro'],
        motions: ['slow'],
        energy: [1, 3],
      },
      captionStyle: 'line',
      textAnimation: 'rise',
      waveform: 'line',
      audioReactive: 'particles',
      lyricMotion: {
        moveMs: 560,
        easing: [0.22, 1, 0.36, 1],
        nextAlpha: 0.6,
        prevAlpha: 0.42,
        dimScale: 0.5,
        nowWord: 'accent',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'serif',
      text: 'serif',
      displayStyle: { weight: 700, italic: false, case: 'none', tracking: -0.02 },
      textStyle: { weight: 400, italic: false, case: 'none', tracking: 0.0 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'mid', L: 0.32, Cx: 1, Cmin: 0.06, Hto: [55, 0.35] },
      surface: { from: 'mid', L: 0.42, Cx: 1.1, Cmin: 0.07, Hto: [55, 0.45] },
      text: { from: 'light', L: 0.94, Cx: 0.5, Hto: [85, 0.5] },
      accent: { from: 'vivid', L: 0.8, Cx: 1.1, Cmin: 0.12, Hto: [65, 0.4] },
    },
    texture: { kind: 'glow', strength: 0.35 },
    wash: { colour: 'accent', blend: 'soft-light', opacity: 0.2 },
    cover: {
      frame: 'arch',
      ground: 'flat',
      ornament: null,
      tiltDeg: null,
      textEffect: null,
    },
  },
  retro: {
    id: 'retro',
    label: 'Retro',
    tileLine: 'Faded and grainy. An old record sleeve.',
    brief: {
      feeling: 'Faded and grainy. An old record sleeve.',
      paceSecPerCut: [1.5, 3],
      cutOn: 'downbeat',
      transitions: ['cut', 'dissolve'],
      segmentMotions: ['static', 'push'],
      artworkMotions: ['drift', 'still'],
      footage: {
        moods: ['melancholic', 'chill'],
        settings: ['road', 'interior', 'nature'],
        subjects: ['people', 'no-people'],
        shotTypes: ['slow-motion', 'wide', 'tracking'],
        motions: ['slow', 'medium'],
        energy: [1, 3],
      },
      captionStyle: 'kinetic',
      textAnimation: 'typewriter',
      waveform: 'bars',
      audioReactive: 'bars',
      lyricMotion: {
        moveMs: 300,
        easing: [0.33, 1, 0.68, 1],
        nextAlpha: 0.58,
        prevAlpha: 0.4,
        dimScale: 0.5,
        nowWord: 'shadow',
        stepFrames: 2,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'script',
      text: 'mono',
      displayStyle: { weight: 400, italic: false, case: 'none', tracking: 0.0 },
      textStyle: { weight: 600, italic: false, case: 'uppercase', tracking: 0.2 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'mid', L: 0.5, Cx: 0.8 },
      surface: { from: 'dark', L: 0.4, Cx: 0.9 },
      text: { from: 'light', L: 0.95, Cx: 0.4, Hto: [90, 0.5] },
      accent: { from: 'dark', L: 0.22, Cx: 0.8 },
    },
    texture: { kind: 'grain', strength: 0.5 },
    wash: { colour: 'surface', blend: 'screen', opacity: 0.15 },
    cover: {
      frame: 'sleeve',
      ground: 'flat',
      ornament: 'bandsRecord',
      tiltDeg: null,
      textEffect: 'hardShadow',
    },
  },
  neon: {
    id: 'neon',
    label: 'Neon',
    tileLine: 'Night lights. Glow on black.',
    brief: {
      feeling: 'Night lights. Tube type that glows on black.',
      paceSecPerCut: [0.75, 2],
      cutOn: 'beat',
      transitions: ['cut', 'flash'],
      segmentMotions: ['push', 'pan-left', 'pan-right'],
      artworkMotions: ['pulse', 'liquid'],
      footage: {
        moods: ['euphoric', 'mysterious'],
        settings: ['city-night', 'abstract', 'studio'],
        subjects: ['no-people', 'performer'],
        shotTypes: ['tracking', 'macro'],
        motions: ['medium', 'fast'],
        energy: [3, 5],
      },
      captionStyle: 'word',
      textAnimation: 'slam',
      waveform: 'ring',
      audioReactive: 'ring',
      lyricMotion: {
        moveMs: 240,
        easing: [0.16, 1, 0.3, 1],
        nextAlpha: 0.62,
        prevAlpha: 0.4,
        dimScale: 0.48,
        nowWord: 'glow',
        stepFrames: null,
        tiltDeg: null,
        swayDeg: null,
      },
    },
    type: {
      display: 'wide',
      text: 'mono',
      displayStyle: { weight: 800, italic: false, case: 'uppercase', tracking: -0.01 },
      textStyle: { weight: 500, italic: false, case: 'uppercase', tracking: 0.34 },
      accentSecondLine: true,
    },
    colours: {
      background: { from: 'vivid', L: 0.1, C: 0.035 },
      surface: { from: 'vivid', L: 0.17, C: 0.05 },
      text: { from: 'vivid', L: 0.76, C: 0.2 },
      accent: { from: 'vivid', L: 0.84, C: 0.16, Hadd: 150 },
    },
    texture: { kind: 'glow', strength: 0.9 },
    wash: null,
    cover: {
      frame: 'tube',
      ground: 'flat',
      ornament: null,
      tiltDeg: null,
      textEffect: 'glow',
    },
  },
  handmade: {
    id: 'handmade',
    label: 'Handmade',
    tileLine: 'Paper, tape and handwriting.',
    brief: {
      feeling: 'Paper, tape and handwriting. Made at a kitchen table.',
      paceSecPerCut: [2, 4],
      cutOn: 'phrase',
      transitions: ['cut'],
      segmentMotions: ['static'],
      artworkMotions: ['still', 'drift'],
      footage: {
        moods: ['chill', 'romantic'],
        settings: ['interior', 'nature', 'studio'],
        subjects: ['no-people'],
        shotTypes: ['macro', 'medium'],
        motions: ['static', 'slow'],
        energy: [1, 3],
      },
      captionStyle: 'line',
      textAnimation: 'wiggle',
      waveform: 'line',
      audioReactive: 'particles',
      lyricMotion: {
        moveMs: 400,
        easing: [0.34, 1.3, 0.64, 1],
        nextAlpha: 0.62,
        prevAlpha: 0.42,
        dimScale: 0.5,
        nowWord: 'marker',
        stepFrames: null,
        tiltDeg: [-1.2, 0.8],
        swayDeg: 0.6,
      },
    },
    type: {
      display: 'hand',
      text: 'hand',
      displayStyle: { weight: 700, italic: false, case: 'none', tracking: 0.0 },
      textStyle: { weight: 500, italic: false, case: 'none', tracking: 0.0 },
      accentSecondLine: false,
    },
    colours: {
      background: { from: 'light', L: 0.935, C: 0.03, Hto: [85, 0.6] },
      surface: { from: 'mid', L: 0.74, Cx: 0.9 },
      text: { from: 'dark', L: 0.22, Cx: 0.7 },
      accent: { from: 'vivid', L: 0.58, Cx: 1.3, Cmin: 0.13 },
    },
    texture: { kind: 'paper', strength: 0.5 },
    wash: null,
    cover: {
      frame: 'photo',
      ground: 'flat',
      ornament: 'tornTape',
      tiltDeg: { cover: -4, title: -4 },
      textEffect: 'marker',
    },
  },
};

export function isReleaseThemeId(value: unknown): value is ReleaseThemeId {
  return typeof value === 'string' && (RELEASE_THEME_IDS as readonly string[]).includes(value);
}

/** The theme id of a stored value. An old style, an old vibe or a broken value gives the default theme. */
export function parseReleaseThemeId(value: unknown): ReleaseThemeId {
  return isReleaseThemeId(value) ? value : DEFAULT_RELEASE_THEME;
}

// ---------------------------------------------------------------------------------------------
// The look of a kit
// ---------------------------------------------------------------------------------------------

/**
 * The stored choice of a kit: the theme, an override of the display font and the logo. The kit
 * row, the copy on each asset and reel, and the account default (`AccountModel.releaseKitLook`)
 * hold it.
 */
export interface ReleaseKitLook {
  themeId: ReleaseThemeId;
  /** Null: the display font of the theme. */
  fontOverride: KitFontId | null;
  logoFileId: number | null;
}

export const DEFAULT_RELEASE_KIT_LOOK: ReleaseKitLook = {
  themeId: DEFAULT_RELEASE_THEME,
  fontOverride: null,
  logoFileId: null,
};

/**
 * The look stored in a JSON column or read from a row. It returns null only for a value that is
 * not an object, so "no look saved" differs from "a look saved". Inside an object every broken or
 * old field reads as its default: an unknown `themeId` (also an old `styleId`) gives the default
 * theme, an unknown `fontOverride` (also an old `fontId`) gives null, a bad `logoFileId` gives null.
 */
export function parseReleaseKitLook(value: unknown): ReleaseKitLook | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const logo = record.logoFileId;
  return {
    themeId: parseReleaseThemeId(record.themeId),
    fontOverride: isKitFontId(record.fontOverride) ? record.fontOverride : null,
    logoFileId: typeof logo === 'number' && Number.isInteger(logo) && logo > 0 ? logo : null,
  };
}

/** The look of a stored value, or the default look when the value holds none. */
export function releaseKitLookOf(value: unknown): ReleaseKitLook {
  return parseReleaseKitLook(value) ?? { ...DEFAULT_RELEASE_KIT_LOOK };
}

/** The two fonts of a look: the display font (the override, else the theme font) and the text font. */
export function themeFonts(look: Pick<ReleaseKitLook, 'themeId' | 'fontOverride'>): {
  display: KitFontId;
  text: KitFontId;
} {
  const { type } = RELEASE_THEMES[look.themeId];
  return { display: look.fontOverride ?? type.display, text: type.text };
}

/** The fonts of a look with the type style of each role, as the renderer applies them. */
export interface ThemeType {
  fonts: { display: KitFontId; text: KitFontId };
  display: ThemeTypeStyle & {
    /** A multiplier for the size of display text: 1 with the theme font; an override keeps the width of the line. */
    scale: number;
  };
  text: ThemeTypeStyle;
}

/**
 * The fonts and the type styles of a look. With a font override the display role keeps the case
 * and the letter spacing of the theme, takes the display weight and the slant of the override
 * font, and scales its size so that a wide font does not run out of the frame. Pure.
 */
export function themeType(look: Pick<ReleaseKitLook, 'themeId' | 'fontOverride'>): ThemeType {
  const { type } = RELEASE_THEMES[look.themeId];
  const fonts = themeFonts(look);
  if (fonts.display === type.display) {
    return { fonts, display: { ...type.displayStyle, scale: 1 }, text: { ...type.textStyle } };
  }
  const own = KIT_FONT_FACES[type.display];
  const override = KIT_FONT_FACES[fonts.display];
  return {
    fonts,
    display: {
      ...type.displayStyle,
      weight: override.displayWeight,
      italic: override.italic,
      scale: Math.round((own.relativeWidth / override.relativeWidth) * 1000) / 1000,
    },
    text: { ...type.textStyle },
  };
}

/** The lines of a title: the first line in the text colour, the second line (or null) in the accent. */
export interface TitleLines {
  first: string;
  /** The line in the accent colour. Null: the theme has no accent line, or the title is empty. */
  second: string | null;
}

/**
 * The title lines of a theme (design q, "The title repeats in the accent"): a theme with
 * `type.accentSecondLine` shows the full title two times, the second time in the accent colour.
 * The title is not cut into words: the design tile repeats the full title (`content: attr(data-t)`).
 * The renderer (`ReelTextLayer`) and the web still (`ThemeStill`) use this one rule. Pure.
 */
export function titleLines(themeId: ReleaseThemeId, title: string): TitleLines {
  return titleLinesOf(RELEASE_THEMES[themeId].type.accentSecondLine, title);
}

/** `titleLines` for a caller that holds the flag and not the theme id (the `theme` prop of the renderer). Pure. */
export function titleLinesOf(accentSecondLine: boolean, title: string): TitleLines {
  const first = title.trim();
  return { first, second: accentSecondLine && first.length > 0 ? first : null };
}

// ---------------------------------------------------------------------------------------------
// Colours: the recipe of the design, in OKLCH
// ---------------------------------------------------------------------------------------------

/** The colours of an asset: the cover palette shaped by the theme. All are `#rrggbb`. */
export interface ThemeColours {
  background: string;
  /** A panel on the background. */
  surface: string;
  /** Text on the background. Contrast at least `TEXT_MIN_CONTRAST`. */
  text: string;
  /** Emphasis, the CTA, the waveform. Contrast at least `ACCENT_MIN_CONTRAST` against the background. */
  accent: string;
  /** Text on an accent shape: the text or the background, the one with the higher contrast on the accent. */
  onAccent: string;
}

/** The WCAG 2 contrast ratio that the text keeps against the background. */
export const TEXT_MIN_CONTRAST = 4.5;
/** The WCAG 2 contrast ratio that the accent keeps against the background. */
export const ACCENT_MIN_CONTRAST = 3;

/** The palette of a song with no readable cover colour (the Music24 inks). */
export const FALLBACK_PALETTE = ['#120a24', '#6417fb', '#ffffff'] as const;

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** A colour in OKLCH: lightness 0..1, chroma, hue in degrees. */
export interface Oklch {
  L: number;
  C: number;
  H: number;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

function toLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function toGamma(channel: number): number {
  return channel <= 0.0031308 ? 12.92 * channel : 1.055 * channel ** (1 / 2.4) - 0.055;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** `#rrggbb` as OKLCH. */
export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { L, C: Math.sqrt(A * A + B * B), H: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

function oklchToLinearRgb(colour: Oklch): [number, number, number] {
  const a = colour.C * Math.cos((colour.H * Math.PI) / 180);
  const b = colour.C * Math.sin((colour.H * Math.PI) / 180);
  const l = (colour.L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (colour.L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (colour.L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** The gamut fit takes at most this many steps; each step takes 5 % off the chroma. */
const GAMUT_MAX_STEPS = 60;
const GAMUT_TOLERANCE = 0.0005;

/** OKLCH as `#rrggbb`. A colour outside sRGB loses 5 % chroma per step until it fits. */
export function oklchToHex(colour: Oklch): string {
  let fitted: Oklch = { L: clamp01(colour.L), C: colour.C, H: colour.H };
  let rgb = oklchToLinearRgb(fitted);
  let steps = 0;
  while (rgb.some((value) => value < -GAMUT_TOLERANCE || value > 1 + GAMUT_TOLERANCE) && steps < GAMUT_MAX_STEPS) {
    fitted = { L: fitted.L, C: fitted.C * 0.95, H: fitted.H };
    rgb = oklchToLinearRgb(fitted);
    steps += 1;
  }
  const channel = (value: number): string =>
    Math.round(clamp01(toGamma(clamp01(value))) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${rgb.map(channel).join('')}`;
}

/** The WCAG relative luminance of a `#rrggbb` colour (0 = black, 1 = white). An invalid colour gives 0. */
export function relativeLuminance(hex: string): number {
  if (!HEX_PATTERN.test(hex)) {
    return 0;
  }
  const [r, g, b] = hexToRgb(hex).map(toLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The WCAG 2 contrast ratio of two `#rrggbb` colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The hue `share` of the short way from `hue` to `target`, in degrees 0..360. */
function hueToward(hue: number, target: number, share: number): number {
  const distance = ((target - hue + 540) % 360) - 180;
  return (hue + distance * share + 360) % 360;
}

/** The cover colours that the rules start from. */
export type CoverRoles = Readonly<Record<ColourRole, Oklch>>;

/**
 * The valid colours of a palette. A palette with no valid colour becomes `FALLBACK_PALETTE`, so a
 * song with no readable cover still gets colours.
 */
function validPalette(palette: readonly string[]): string[] {
  const colours = palette.filter((hex) => HEX_PATTERN.test(hex));
  return colours.length === 0 ? [...FALLBACK_PALETTE] : colours;
}

/**
 * The roles of a cover palette: the lightest colour, the middle one by lightness, the darkest one,
 * and the one with the most chroma. Pure.
 */
export function coverRoles(palette: readonly string[]): CoverRoles {
  const byLightness = validPalette(palette)
    .map(hexToOklch)
    .sort((a, b) => b.L - a.L);
  const first = byLightness[0] as Oklch;
  return {
    light: first,
    mid: byLightness[Math.floor(byLightness.length / 2)] ?? first,
    dark: byLightness[byLightness.length - 1] ?? first,
    vivid: [...byLightness].sort((a, b) => b.C - a.C)[0] ?? first,
  };
}

/** One rule applied to the roles of a cover: the colour of a slot before the contrast fit. Pure. */
export function shapeColour(roles: CoverRoles, rule: ColourRule): Oklch {
  const source = roles[rule.from];
  let C = rule.C !== undefined ? rule.C : source.C * (rule.Cx !== undefined ? rule.Cx : 1);
  if (rule.Cmin !== undefined) {
    C = Math.max(C, rule.Cmin);
  }
  if (rule.Cmax !== undefined) {
    C = Math.min(C, rule.Cmax);
  }
  let H = source.H + (rule.Hadd ?? 0);
  if (rule.Hto !== undefined) {
    H = hueToward(H, rule.Hto[0], rule.Hto[1]);
  }
  return { L: rule.L, C, H: (H + 360) % 360 };
}

/** The contrast fit takes at most this many steps. */
const CONTRAST_MAX_STEPS = 30;

/**
 * Moves a colour away from the background until the contrast is at least `minimum`: each step
 * moves the lightness 0.02 and takes 3 % off the chroma. Pure.
 */
function fitContrast(colour: Oklch, backgroundHex: string, minimum: number): Oklch {
  let fitted: Oklch = { ...colour };
  const direction = fitted.L >= hexToOklch(backgroundHex).L ? 1 : -1;
  let steps = 0;
  while (contrastRatio(oklchToHex(fitted), backgroundHex) < minimum && steps < CONTRAST_MAX_STEPS) {
    fitted = { L: fitted.L + direction * 0.02, C: fitted.C * 0.97, H: fitted.H };
    steps += 1;
  }
  return fitted;
}

/**
 * The colours of an asset: the cover palette shaped by the recipe of the theme. Pure. It is the
 * recipe code of the design page (`themeColours` in its logic class), line for line. No slot holds
 * a fixed theme colour: every colour starts from a cover colour (or from `FALLBACK_PALETTE` when
 * the palette has no valid colour). The text keeps `TEXT_MIN_CONTRAST` and the accent keeps
 * `ACCENT_MIN_CONTRAST` against the background.
 */
export function themeColours(themeId: ReleaseThemeId, palette: readonly string[]): ThemeColours {
  const recipe = RELEASE_THEMES[themeId].colours;
  const roles = coverRoles(palette);
  const background = oklchToHex(shapeColour(roles, recipe.background));
  const surface = oklchToHex(shapeColour(roles, recipe.surface));
  const text = oklchToHex(fitContrast(shapeColour(roles, recipe.text), background, TEXT_MIN_CONTRAST));
  const accent = oklchToHex(fitContrast(shapeColour(roles, recipe.accent), background, ACCENT_MIN_CONTRAST));
  const onAccent = contrastRatio(text, accent) >= contrastRatio(background, accent) ? text : background;
  return { background, surface, text, accent, onAccent };
}

/**
 * A faded cover colour for a decoration (the bands and the record of Retro): one rule applied to
 * the cover palette, with no contrast fit. Pure.
 */
export function coverShade(palette: readonly string[], rule: ColourRule): string {
  return oklchToHex(shapeColour(coverRoles(palette), rule));
}

/**
 * The colour of text on a filled shape of `colour`: the candidate with the higher contrast. Pure.
 */
export function readableOn(colour: string, candidates: readonly [string, string]): string {
  return contrastRatio(candidates[0], colour) >= contrastRatio(candidates[1], colour) ? candidates[0] : candidates[1];
}
